import React from 'react';
import {
	Breakpoints,
	Container,
	ContainerGutter,
	ContainerPadding,
	CTA,
	Layout,
	Text,
	TextAlignment,
	TokenTextAppearance,
} from '@volkswagen-onehub/components-core';
import { DataCollectionCTAs } from '../components/dataCollectionCTAs';
import { CtaType } from '@esportal/portal-service';
import { PortalContext } from '../../../context/PortalContext';
import { getUserName } from '../../../helpers/nameHelper';
import {
	DataCollectionStep,
	EndFormBackgroundContainer,
	formLayout,
	RelativeContainer,
	StyledIcon,
} from '../components/dataCollectionForm';
import { RoadLoverIcon } from './forms-icons/road-lover-icon';
import { useTrackingManager } from '../../../tracking/useTrackingManager';
import { formatOwnership } from '../../../tracking/trackingHelper';
import { FormDataCollection } from './types';

interface StepsState {
	previousStep: number;
	actualStep: number;
}

interface Form1Props {
	handleLinealNextStep: (answer?: string[]) => void;
	updateVisibleSteps: (newSteps: string[], isSelected: boolean) => void;
	getInitialSteps: (steps: DataCollectionStep[]) => void;
	steps: StepsState;
}

export const Form1: React.FC<Form1Props> = ({ handleLinealNextStep, updateVisibleSteps, getInitialSteps, steps }) => {
	const { portalService } = React.useContext(PortalContext);

	const formName = 'vw-club-data_collection-1';

	const userData = portalService.getPerson().getValue();
	const userName = getUserName(userData);

	const userVehicles = portalService.getPersonVehicles().getValue();
	const [, setAnswer] = React.useState<string[]>([]);
	const [isFormLoadTracked, setIsFormLoadTracked] = React.useState<boolean>(false);

	const trackingManager = useTrackingManager();

	const handleAnswerChange = (value: string[]) => {
		setAnswer(value);
	};

	React.useEffect(() => {
		if (userVehicles && steps.actualStep !== 0 && !isFormLoadTracked) {
			trackingManager.trackClubVWFormLoad(
				{
					FormName: formName,
					FormStart: true,
					FormTypeMultiple: ['otherform'],
				},
				{ contentId: formName },
				{ vehicle: formatOwnership(userVehicles?.vehicles) },
			);
			setIsFormLoadTracked(true);
		}
	}, [userVehicles, steps, isFormLoadTracked]);

	const form1: FormDataCollection[] = [
		{
			name: 'INTRO_FORM',
			screen: (
				<Container wrap={'always'}>
					<Container wrap={'always'}>
						<Container
							padding={{
								bottom: ContainerPadding.static200,
							}}
						>
							<Text appearance={TokenTextAppearance.headline400}>
								¡Queremos <Text bold>adaptarnos a ti!</Text>
							</Text>
						</Container>
						<Container
							padding={{
								bottom: {
									[Breakpoints.default]: ContainerPadding.static500,
									[Breakpoints.b960]: ContainerPadding.static520,
								},
							}}
						>
							<Text appearance={TokenTextAppearance.bigcopy100}>
								Cada conductor, cada pasajero y cada viaje es único. Y tu experiencia con nosotros, también.
							</Text>
							<Text appearance={TokenTextAppearance.bigcopy100}>
								Cuanto más sepamos de ti, más personalizada será. Así que, ¿nos conocemos?
							</Text>
						</Container>
						<Container>
							<CTA
								tag="button"
								emphasis="primary"
								onClick={() => {
									handleLinealNextStep();
									trackingManager.trackClubVWButtonClick(
										'Empezar',
										{ contentId: `Start ${formName}` },
										{ vehicle: formatOwnership(userVehicles?.vehicles) },
									);
								}}
							>
								<Text>Empezar</Text>
							</CTA>
						</Container>
					</Container>
				</Container>
			),
			userLevel: 1,
			visibleStep: true,
		},
		{
			name: 'USO_UTILIZACION',
			screen: (
				<Container wrap={'always'}>
					<Container
						padding={{
							bottom: ContainerPadding.static150,
						}}
					>
						<Text appearance={TokenTextAppearance.bigcopy200}>Tenemos mucha curiosidad...</Text>
					</Container>
					<Container>
						<Text appearance={TokenTextAppearance.headline400}>
							<Text bold>¿Para qué utilizas </Text> normalmente tu Volkswagen?
						</Text>
					</Container>
					<DataCollectionCTAs
						type={CtaType.RADIO}
						ctaAnswers={[
							{ label: '⏰ Para ir a trabajar', value: 'trabajar' },
							{ label: '🛒 Para otros trayectos del día a día', value: 'diaadia' },
							{ label: '🏖️ Para vacaciones y fines de semana', value: 'vacaciones' },
							{ label: '🏡 Para llevar a mi pareja o a los peques', value: 'familia' },
							{ label: '🎨 Para disfrutar de mis aficiones', value: 'aficiones' },
							{ label: '🛣️ Por el simple Placer de conducir', value: 'placer' },
							{ label: '🔄 Otros', value: 'otro' },
						]}
						updateVisibleSteps={updateVisibleSteps}
						handleLinealNextStep={handleLinealNextStep}
						handleAnswerChange={handleAnswerChange}
						stepName="USO_UTILIZACION"
						userLevel={1}
					/>
				</Container>
			),
			userLevel: 1,
			visibleStep: true,
		},
		{
			name: 'USO_TIEMPO',
			screen: (
				<Container wrap={'always'}>
					<Container
						padding={{
							bottom: ContainerPadding.static200,
						}}
					>
						<Text appearance={TokenTextAppearance.bigcopy200}>Veamos si tu Volkswagen es tu segunda casa...</Text>
					</Container>
					<Container>
						<Text appearance={TokenTextAppearance.headline400}>
							<Text bold>¿Cuánto tiempo </Text> pasas en él?
						</Text>
					</Container>
					<DataCollectionCTAs
						type={CtaType.RADIO}
						ctaAnswers={[
							{ label: '🌅 Conduzco a diario', value: 'diario' },
							{ label: '🗓️ Conduzco algunas veces a la semana', value: 'semana' },
							{ label: '📅 Conduzco algunas veces al mes', value: 'mes' },
							{ label: '🚀 Conduzco algunas veces al año', value: 'ano' },
						]}
						updateVisibleSteps={updateVisibleSteps}
						handleLinealNextStep={handleLinealNextStep}
						handleAnswerChange={handleAnswerChange}
						stepName="USO_TIEMPO"
						userLevel={1}
					/>
				</Container>
			),
			userLevel: 1,
			visibleStep: true,
		},
		{
			name: 'USO_HORAS',
			screen: (
				<Container wrap={'always'}>
					<Container
						padding={{
							bottom: ContainerPadding.static200,
						}}
					>
						<Text appearance={TokenTextAppearance.bigcopy200}>Veamos si tu Volkswagen es tu segunda casa...</Text>
					</Container>
					<Container>
						<Text appearance={TokenTextAppearance.headline400}>
							En total ¿Cuántas <Text bold> horas son a la semana?</Text>
						</Text>
					</Container>
					<DataCollectionCTAs
						type={CtaType.RADIO}
						ctaAnswers={[
							{ label: '🛣️ Menos de 5h', value: '5' },
							{ label: '🏞️ De 5h a 10h', value: '5-10' },
							{ label: '🌄 De 10h a 15h', value: '10-15' },
							{ label: '🌍 Más de 15h', value: '15' },
						]}
						updateVisibleSteps={updateVisibleSteps}
						handleLinealNextStep={handleLinealNextStep}
						handleAnswerChange={handleAnswerChange}
						stepName="USO_HORAS"
						userLevel={1}
					/>
				</Container>
			),
			userLevel: 1,
			visibleStep: true,
		},
		{
			name: 'END_FORM',
			screen: (
				<EndFormBackgroundContainer className="end-form-1__background">
					<Layout data-testid="end-form__layout" appearance={formLayout} allowOverflowingContent>
						<RelativeContainer className="end-form__relative-container">
							<Container
								verticalAlign={'center'}
								horizontalAlign={'center'}
								wrap={{ [Breakpoints.default]: 'always', [Breakpoints.b960]: 'never' }}
								gutter={{ [Breakpoints.b960]: ContainerGutter.grid001 }}
								shrinkContent
							>
								<Container
									horizontalAlign={'center'}
									verticalAlign={'center'}
									padding={{
										bottom: {
											[Breakpoints.default]: ContainerPadding.static200,
											[Breakpoints.b960]: ContainerPadding.static0,
										},
									}}
								>
									<StyledIcon className="icon-container">
										<RoadLoverIcon />
									</StyledIcon>
								</Container>
								<Container
									horizontalAlign={{ [Breakpoints.default]: 'center', [Breakpoints.b960]: 'flex-start' }}
									verticalAlign={'center'}
									wrap={'always'}
								>
									<Container
										horizontalAlign={{ [Breakpoints.default]: 'center', [Breakpoints.b960]: 'flex-start' }}
										verticalAlign={'center'}
										padding={{
											bottom: ContainerPadding.static200,
										}}
										wrap={'always'}
										shrinkContent
									>
										<Text
											appearance={TokenTextAppearance.headline400}
											textAlign={{
												[Breakpoints.default]: TextAlignment.center,
												[Breakpoints.b960]: TextAlignment.left,
											}}
											bold
										>
											¡Ya eres Road lover!
										</Text>
									</Container>
									<Container
										horizontalAlign={{ [Breakpoints.default]: 'center', [Breakpoints.b960]: 'flex-start' }}
										verticalAlign={'center'}
										wrap={'always'}
										padding={{
											bottom: {
												[Breakpoints.default]: ContainerPadding.static200,
											},
										}}
									>
										<Text
											appearance={TokenTextAppearance.bigcopy100}
											textAlign={{
												[Breakpoints.default]: TextAlignment.center,
												[Breakpoints.b960]: TextAlignment.left,
											}}
										>
											Nos encanta conocerte, {userName}. Nos ayuda mucho para contarte solo cosas que te interesen en el
											futuro.
										</Text>
									</Container>
									<Container
										horizontalAlign={{ [Breakpoints.default]: 'center', [Breakpoints.b960]: 'flex-start' }}
										verticalAlign={'center'}
										wrap={'always'}
										padding={{
											bottom: {
												[Breakpoints.default]: ContainerPadding.static500,
												[Breakpoints.b960]: ContainerPadding.static520,
											},
										}}
									>
										<Text
											appearance={TokenTextAppearance.headline200}
											textAlign={{
												[Breakpoints.default]: TextAlignment.center,
												[Breakpoints.b960]: TextAlignment.left,
											}}
											bold
										>
											¿Quieres seguir respondiendo y subir de nivel?
										</Text>
									</Container>
									<Text
										textAlign={{
											[Breakpoints.default]: TextAlignment.center,
											[Breakpoints.b960]: TextAlignment.left,
										}}
									>
										<CTA
											tag="button"
											emphasis="primary"
											onClick={() => {
												handleLinealNextStep();
												trackingManager.trackClubVWButtonClick(
													'A por el siguiente nivel',
													{
														contentId: 'Form-1 completed',
													},
													{ vehicle: formatOwnership(userVehicles?.vehicles) },
												);
											}}
										>
											A por el siguiente nivel
										</CTA>
									</Text>
								</Container>
							</Container>
						</RelativeContainer>
					</Layout>
				</EndFormBackgroundContainer>
			),
			userLevel: 1,
			visibleStep: true,
		},
	];

	React.useEffect(() => {
		const findDataCollectionCTAs = (children: any) => {
			let ctaAnswers: any = [];

			React.Children.forEach(children, (child) => {
				if (!child) return;

				if (child.type === DataCollectionCTAs) {
					ctaAnswers.push(...(child.props.ctaAnswers || []));
				}

				if (child.props && child.props.children) {
					ctaAnswers = ctaAnswers.concat(findDataCollectionCTAs(child.props.children));
				}
			});

			return ctaAnswers;
		};

		const stepNamesAndVisibility = form1.map((step) => {
			const ctaAnswers = findDataCollectionCTAs(step.screen.props.children);

			return {
				name: step.name,
				visibleStep: step.visibleStep,
				questionLevel: step.userLevel,
				ctaAnswers: ctaAnswers.length > 0 ? ctaAnswers : null,
			};
		});
		getInitialSteps(stepNamesAndVisibility);
		setAnswer([]);
	}, []);

	const currentFormScreen = form1[steps.actualStep].screen;

	return <>{currentFormScreen}</>;
};
